import { getRequestGeneric, postRequestGeneric, putRequestGeneric } from "@/services/backendApiGeneric";

export interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  priceLabel?: string;
  billing: string;
  trialDays?: number;
  perLocation?: boolean;
  maxUsersPerLoc?: number;
  minUsersPerLoc?: number;
  seatsPerLocation?: number;
  uncappedSeats?: boolean;
  popular?: boolean;
  surcharge?: boolean;
  availableFor?: string[];
  /** Number of users included in the base price per location (Enterprise: 5). */
  includedUsers?: number;
  /** Net monthly price per additional user beyond `includedUsers` (Enterprise: 4.99). */
  extraUserPrice?: number;
  features: string[];
}

export interface SubscriptionInfo {
  id?: number;
  plan: string;
  status: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
}

export interface LicensedTier {
  id: string;
  label: string;
  quantity: number;
  seatsPerUnit: number;
  /** Aggregate seat cap for this tier (Quantity * SeatsPerUnit + booked extra users for Enterprise). */
  totalSeats?: number;
  /** @deprecated Enterprise no longer has uncapped seats. Always false on responses from current backend. */
  uncappedSeats?: boolean;
}

export interface LicenseOverview {
  plan: string;
  planLabel: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  isTrialing: boolean;
  licensedBasis: number;
  licensedEnterprise: number;
  licensedLocations: number;
  usedLocations: number;
  remainingLocations: number;
  licensedUsers: number;
  usedUsers: number;
  remainingUsers: number;
  locationsFull: boolean;
  usersFull: boolean;
  canUpgrade: boolean;
  features: string[];
  tiers: LicensedTier[];
  businessType: string;
  /** Von der Stripe-Paywall befreit: keine Mitarbeiter- oder Standortgrenze. */
  paywallExempt: boolean;
  /** Abgefragtes Unternehmen (Spec 2026-10-21); fehlt bei der Organisation. */
  idCompany?: number;
  /** false: eigene Abrechnung, keine Paketgrenze. */
  billedViaOrganization?: boolean;
}

export interface Invoice {
  id: string;
  number: string;
  status: string;
  currency: string;
  amountPaid: number;
  amountDue: number;
  total: number;
  created: number;
  periodStart?: number;
  periodEnd?: number;
  hostedUrl?: string;
  pdfUrl?: string;
  description?: string;
}

export interface QuotaViolation {
  code: "license_user_quota_exceeded" | "license_location_quota_exceeded";
  message: string;
  plan: string;
  licensedLocations: number;
  usedLocations: number;
  licensedUsers: number;
  usedUsers: number;
}

export function getPlans(businessType?: string): Promise<Plan[]> {
  const qs = businessType ? `?businessType=${encodeURIComponent(businessType)}` : "";
  return getRequestGeneric(`/protected/billing/plans${qs}`);
}

export function getInvoices(idCompany?: number): Promise<Invoice[]> {
  const qs = idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
  return getRequestGeneric(`/protected/billing/invoices${qs}`);
}

export function getSubscription(): Promise<SubscriptionInfo> {
  return getRequestGeneric("/protected/billing/subscription");
}

/** `?idCompany=…` für Aufrufe im Scope eines Unternehmens (Spec 2026-10-21). */
function companyQs(idCompany?: number | null): string {
  return idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
}

export function getLicense(idCompany?: number | null): Promise<LicenseOverview> {
  return getRequestGeneric(`/protected/billing/license${companyQs(idCompany)}`);
}

export function createCheckoutSession(plan: string): Promise<{ url: string }> {
  return postRequestGeneric("/protected/billing/checkout", { plan }, true);
}

/**
 * Administrator only (sonst 403). Für ein Unternehmen mit eigener Abrechnung
 * antwortet das Backend mit 409 (keine Paketgrenze, kein Upgrade).
 */
export function createUpgradeSession(idCompany?: number | null): Promise<{ url: string }> {
  return postRequestGeneric(`/protected/billing/upgrade${companyQs(idCompany)}`, {}, true);
}

/** Administrator only (sonst 403). Mit idCompany der Kunde des Unternehmens. */
export function createPortalSession(idCompany?: number | null): Promise<{ url: string }> {
  return postRequestGeneric(`/protected/billing/portal${companyQs(idCompany)}`, {}, true);
}

/** Abrechnungsstatus eines Unternehmens (GET /billing/company-status). */
export interface CompanyBillingStatus {
  idCompany: number;
  name: string;
  /** Über das Organisations-Abo abgerechnet. */
  billedViaOrganization: boolean;
  /** Für jede abrechenbare App eingerichtet (Organisation, Befreiung, eigenes Abo oder Zahlungsmittel). */
  billingSetUp: boolean;
  /** Eingerichtet je abrechenbarer App. */
  apps: Record<string, boolean>;
  /** Zahlungsmittel war nicht prüfbar (Stripe-Störung); gilt als nicht eingerichtet. */
  unknown?: boolean;
}

/**
 * Administrator only. Mit idCompany nur dieses Unternehmen (502, wenn
 * dessen Zahlungsmittel nicht prüfbar ist).
 */
export function getCompanyBillingStatuses(idCompany?: number | null): Promise<CompanyBillingStatus[]> {
  return getRequestGeneric(`/protected/billing/company-status${companyQs(idCompany)}`);
}

/**
 * Rechnungsprofil (Stripe-Customer), das direkt in KARS gepflegt wird
 * (Kundenfeedback 17.08.26, Punkt 2). Stripe bleibt Source of Truth.
 */
export interface BillingInfo {
  /** false = Organisation ohne Stripe-Kunde (z.B. Solo-Plan). */
  hasCustomer: boolean;
  companyName: string;
  email: string;
  line1: string;
  line2: string;
  postalCode: string;
  city: string;
  country: string;
  /** USt-IdNr (DE…) oder Steuernummer. */
  taxId: string;
  /**
   * Kundenfeedback 26.10.2026, III: „Name, Anschrift“ des Unternehmens, für
   * das die Leistung erbracht wird – nur gesetzt, wenn der
   * Rechnungsempfänger davon abweicht. Steht so auf jeder Rechnung unter
   * „Leistung erbracht für“.
   */
  serviceRecipient?: string;
}

export type BillingInfoUpdate = Omit<BillingInfo, "hasCustomer" | "serviceRecipient">;

export function getBillingInfo(idCompany?: number): Promise<BillingInfo> {
  const qs = idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
  return getRequestGeneric(`/protected/billing/billing-info${qs}`);
}

export function updateBillingInfo(info: BillingInfoUpdate, idCompany?: number): Promise<BillingInfo> {
  const qs = idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
  return putRequestGeneric(`/protected/billing/billing-info${qs}`, info, true);
}

/**
 * Zahlungsmethode direkt in KARS verwalten (Spec 2026-08-17): Anzeige und
 * Austausch der Standard-Zahlungsmethode ohne Absprung ins Stripe-Portal.
 */
export interface PaymentMethodInfo {
  type: "card" | "sepa_debit" | "paypal";
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  email?: string;
}

export interface PaymentMethodState {
  /** false = Organisation ohne Stripe-Kunde (z.B. Solo-Plan). */
  hasCustomer: boolean;
  paymentMethod: PaymentMethodInfo | null;
}

export function getPaymentMethod(idCompany?: number): Promise<PaymentMethodState> {
  const qs = idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
  return getRequestGeneric(`/protected/billing/payment-method${qs}`);
}

export function createPaymentMethodSetup(idCompany?: number): Promise<{ clientSecret: string; publishableKey: string }> {
  const qs = idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
  return postRequestGeneric(`/protected/billing/payment-method/setup${qs}`, {}, true);
}

export function confirmPaymentMethod(setupIntentId: string, idCompany?: number): Promise<PaymentMethodState> {
  const qs = idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : "";
  return postRequestGeneric(`/protected/billing/payment-method/confirm${qs}`, { setupIntentId }, true);
}
